import React, { useState } from "react";
import { trackSelectItem, trackViewItem } from "services/analytics";
import Image from "components/image";
import navigate from "helpers/navigate";
import { openKlaviyoForm } from "utils/klaviyo";
import { ReactComponent as Star } from "assets/svgs/star.svg";
import ProductPrices from "../prices";
import Money from "components/money";
import { useGlobalContext } from "context/global";
import clsx from "clsx/lite";
import { getRgba } from "helpers/get-rgba";
import { useSelector } from "react-redux";
import extractIdFromGid from "helpers/extract-gid";
import { ReactComponent as InfoIcon } from "assets/svgs/info.svg";

const Label = ({ label }) => {
	if (!label?.text) return null;
	return (
		<div style={{ backgroundColor: getRgba(label?.bgColor) }}
			className="rounded-5 w-fit text-sm py-1 px-2 absolute bottom-[4%] left-[4%]">
			{label.text}
		</div>
	);
};

const ProductSummary = ({ product, openModal, hideATCPrice, children, newLayout }) => {
	const {
		images = [], image, title, _type, shortDescription = "", handle, earlyAccess, details,
		requireLoginToBuy, klaviyoFormId, label, reviewAggregate, offerPrice, variant
	} = product;
	const { averageRating, reviewCount } = reviewAggregate || {};
	const [activeImage, setActiveImage] = useState(0);
	const { translation, isSubscriptionPage, isCollection } = useGlobalContext();
	const isBundle = _type === "productBundle";
	const { lines } = useSelector((state) => state.account.subscription);

	const cartItem = lines[extractIdFromGid(variant?.id)] || {};

	const handleOnProductClick = () => {
		trackSelectItem(product);
		trackViewItem(product);

		if (earlyAccess && klaviyoFormId)
			openKlaviyoForm(klaviyoFormId);
		else if ((isSubscriptionPage && !isBundle) || hideATCPrice)
			openModal();
		else if ((isSubscriptionPage && isBundle))
			navigate(`/products/${handle}-sub`);
		else if (!isBundle)
			navigate(`/products/${handle}`, { state: { modal: true, title } });
		else
			navigate(`/products/${handle}`);

		setActiveImage(0);
	};

	const hideOfferPrice = isSubscriptionPage && isBundle;

	const showNewLayout = isCollection || newLayout;

	const Badges = () => {
		return <div className="flex flex-col md:flex-row gap-1 flex-wrap">
			{(variant?.stock === "one-time" || variant?.isOneTimeAdded) &&
				<span className="one-time-badge">
					{translation.cart?.oneTime}
				</span>}
			{variant?.discountedPrice === 0 && +cartItem.price === 0 &&
				<span className="text-xs bg-[#97AB51] text-white w-max rounded-xl px-3 py-1">
					{translation?.product?.freeGift}
				</span>}
		</div>;
	};

	return (
		<div className={clsx(showNewLayout && "flex md:flex-col justify-between md:h-full")}>
			{images?.length > 1 ? (
				<div
					className={clsx("cursor-pointer relative", showNewLayout ? "w-[48%] h-[48%] md:h-full md:w-full" : "w-full")}
					onClick={handleOnProductClick}
					onMouseOver={() => setActiveImage(1)}
					onMouseLeave={() => setActiveImage(0)}
					role="button"
					aria-label="View product"
					tabIndex={0}
				>
					<div className="absolute top-2 right-2 z-10 rounded-full bg-white/50 size-8 flex items-center justify-center">
						<InfoIcon />
					</div>
					<Image to={null} image={images[activeImage].asset} className={clsx("w-full", showNewLayout && "rounded-5")} />
					{showNewLayout &&
						<>
							<Label label={label} />
							<div className="w-fit text-sm py-1 px-2 absolute top-[4%] left-[4%]"><Badges /></div>
						</>}
				</div>
			) : image &&
			<div className={clsx("relative", showNewLayout ? "w-[48%] md:w-full" : "w-full")}>
				<img src={image} alt={title} className={clsx("w-full", showNewLayout && "rounded-5")} />
				{showNewLayout &&
					<>
						<Label label={label} />
						<div className="w-fit text-sm py-1 px-2 absolute bottom-[4%] left-[4%]"><Badges /></div>
					</>}
			</div>
			}
			<div className={clsx(showNewLayout ? "w-[48%] md:w-full md:flex md:flex-col md:h-full" : "w-full")}>
				<div onClick={handleOnProductClick}
					className={clsx(
						"flex justify-between items-baseline gap-3",
						showNewLayout && "md:flex-1 text-black")} role="button" tabIndex={0}
				>
					<div className={
						clsx("font-body hover:underline cursor-pointer md:pt-3",
							showNewLayout ? "text-left min-h-10 md:min-h-fit" : "text-center pt-2")}
					>
						{title}
					</div>
					{showNewLayout && !offerPrice && !hideATCPrice &&
						<ProductPrices
							className="text-xs md:text-sm" variant={variant} hideFreeLabel />}
				</div>
				{showNewLayout ? <div>
					{shortDescription &&
						<div className="my-2.5 mx-auto font-base text-black-mana text-xs md:text-sm min-h-10 text-left">
							{shortDescription}
						</div>}
					<div className={
						clsx("product-reviews flex items-center md:min-h-6 flex-wrap",
							reviewCount > 0 ? "justify-between" : "justify-start md:justify-end")}
					>
						{reviewCount > 0 && <div className="flex items-center">
							<Star />
							<span className="text-small pr-1">{averageRating}</span>
						</div>}
						<div className="flex justify-between md:px-1 gap-4">
							{["protein", "kcal"].map(key => (
								details?.nutrients?.[key] && <div key={key} className="flex text-center items-baseline gap-1.5">
									<span className="text-tiny uppercase text-black-mana">{key}</span>
									<span className="text-small">{details?.nutrients[key]}{key === "protein" && "g"}</span>
								</div>
							))}
						</div>
						{offerPrice &&
							<div className="flex flex-row items-center gap-2 text-xs md:text-sm">
								{!hideOfferPrice &&
									<div className="font-boldParagraph">
										<Money value={offerPrice} />
									</div>}
								<div role="text" className={clsx(offerPrice && !hideOfferPrice && "line-through")}
									aria-label={`Original price: ${variant?.compareAtPrice > variant?.price ?
										variant?.compareAtPrice : variant?.price}`}>
									<Money value={variant?.compareAtPrice > variant?.price ?
										variant?.compareAtPrice :
										variant?.price} />
								</div>
							</div>
						}
					</div>
					{children}
				</div> :
					<div className="text-center">
						{shortDescription && <div className={clsx(
							"text-left font-base text-secondary text-sm", variant?.isOneTimeAdded ? "mb-0" : "mb-3.5 mt-2.5")
						}>
							{shortDescription}
						</div>}
						<Badges />
						{!hideATCPrice &&
							<ProductPrices
								variant={variant}
								requireLoginToBuy={requireLoginToBuy}
							/>}
						<div className="mt-5">{children}</div>
					</div>}
			</div>
		</div>
	);
};

export default ProductSummary;